import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Game, Player } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

const Vote: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [username, setUsername] = useState('');
  const [selectedMol, setSelectedMol] = useState<string | null>(null);
  const [points, setPoints] = useState(1);
  const [episodeNumber, setEpisodeNumber] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGame();
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}`);
      const data = await res.json();
      setGame(data);
      if (data.episodes.length > 0) {
        setEpisodeNumber(data.episodes.length);
      }
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  };

  const getActivePlayers = () => {
    if (!game) return [];
    return game.players.filter(p => !p.isEliminated);
  };

  const submitVote = async () => {
    if (!game || !selectedMol || !username.trim()) {
      alert('Vul je naam in en selecteer een mol!');
      return;
    }

    const molPlayer = game.players.find(p => p.id === selectedMol);
    if (!molPlayer) return;

    try {
      await fetch(`${API_URL}/api/games/${game.id}/votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oddsAgainstPlayerId: selectedMol,
          oddsAgainstPlayerName: molPlayer.name,
          suspectedMolId: selectedMol,
          points: points,
          episodeNumber: episodeNumber,
          oddsUsername: username
        })
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting vote:', error);
    }
  };

  if (!game) {
    return <div className="page loading">Laden...</div>;
  }

  if (submitted) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Stem Opgeslagen!</h1>
          <p>Je stem is succesvol geregistreerd</p>
        </div>
        
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>✓</div>
          <h2>Bedankt, {username}!</h2>
          <p style={{ color: 'var(--mol-text-muted)', marginTop: '10px' }}>
            Je hebt {points} punt{points > 1 ? 'en' : ''} ingezet op {game.players.find(p => p.id === selectedMol)?.name}
          </p>
        </div>

        <div className="nav-bar">
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setSubmitted(false);
              setSelectedMol(null);
              setPoints(1);
            }}
          >
            Nog een stem
          </button>
          <button 
            className="btn btn-accent" 
            onClick={() => navigate(`/game/${gameId}/scoreboard`)}
          >
            Bekijk Scorebord
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Wie is de Mol?</h1>
        <p>Zet je punten in op de mol!</p>
      </div>

      <div className="card">
        <h2>Jouw Naam</h2>
        <div className="form-group">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Vul je naam in"
          />
        </div>
      </div>

      <div className="card">
        <h2>Aflevering</h2>
        <div className="form-group">
          <select 
            value={episodeNumber} 
            onChange={(e) => setEpisodeNumber(Number(e.target.value))}
          >
            {game.episodes.length === 0 ? (
              <option value={1}>Aflevering 1</option>
            ) : (
              game.episodes.map(ep => (
                <option key={ep.id} value={ep.number}>
                  Aflevering {ep.number}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div className="card">
        <h2>Kies de Mol</h2>
        <div className="vote-grid">
          {getActivePlayers().map((player: Player) => (
            <div 
              key={player.id}
              className={`vote-card ${selectedMol === player.id ? 'selected' : ''}`}
              onClick={() => setSelectedMol(player.id)}
            >
              <div 
                className="vote-avatar"
                style={{ backgroundColor: player.color }}
              >
                {player.name.charAt(0).toUpperCase()}
              </div>
              <div>{player.name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Hoeveel punten?</h2>
        <p style={{ color: 'var(--mol-text-muted)', marginBottom: '15px', fontSize: '0.9rem' }}>
          Meer punten = meer risico, maar ook meer winst als je gelijk hebt!
        </p>
        <div className="points-selector">
          {[1, 2, 3, 4, 5].map(p => (
            <button
              key={p}
              className={`points-btn ${points === p ? 'selected' : ''}`}
              onClick={() => setPoints(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="nav-bar" style={{ marginTop: '20px' }}>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate(`/game/${gameId}`)}
        >
          ← Terug
        </button>
        <button 
          className="btn btn-accent"
          onClick={submitVote}
          disabled={!selectedMol || !username.trim()}
          style={{ flex: 1 }}
        >
          Stem Insturen
        </button>
      </div>
    </div>
  );
};

export default Vote;
